"use client";

import React from "react";
import { ArrowRight, CircleCheck as CheckCircle2, Layers, PackageCheck, ShieldCheck } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
interface CategoryItem {
  id: string;
  title: string;
  badge: string;
  description: string;
  useCase: string;
  specifications: string;
  imageSrc: string;
  imageSrcset: string;
  imageSizes: string;
  imageAlt: string;
  linkTo: string;
  iconName: string;
}
const categories: CategoryItem[] = [{
  id: "sample-collection-kits",
  title: "Sample Collection Kits",
  badge: "Sampling & Verification",
  description: "Sterile and traceable sampling assemblies engineered for representative raw material verification and quality audit workflows.",
  useCase: "Designed for regulated warehouse intake, quarantine testing, and cleanroom material transfer where sample integrity is strictly audited.",
  specifications: "Gamma-irradiated options, USP Class VI materials, lot-traceable documentation.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/sample_collection_kits_lab.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/sample_collection_kits_lab.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/sample_collection_kits_lab.png-wvc-sizes",
  imageAlt: "Sterile sample collection tools and sealed transfer containers",
  linkTo: "/ProductsPage",
  iconName: "package"
}, {
  id: "field-consumables",
  title: "Field Consumables",
  badge: "Process Analytical Consumables",
  description: "Precision disposable optical vials, cuvettes, calibration reference disks, and certified solvent recovery filters.",
  useCase: "Applied during routine spectrometer verification, at-line tablet testing runs, and continuous process analytical validation routines.",
  specifications: "Optical-grade borosilicate, certified wavelength standards, chemical-resistant seals.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/analytical_field_consumables.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/analytical_field_consumables.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/analytical_field_consumables.png-wvc-sizes",
  imageAlt: "Spectroscopy cuvettes, optical measurement vials, and calibration accessories",
  linkTo: "/PowderTestingAccessoriesPage",
  iconName: "layers"
}, {
  id: "protective-accessories",
  title: "Protective & Operational Accessories",
  badge: "Instrument Hardware",
  description: "Ruggedized analyzer enclosures, optical probe shields, ATEX-compatible holders, and cleanroom transport cases.",
  useCase: "Employed by field teams and manufacturing line technicians to protect handheld Raman/NIR spectrometers across harsh processing environments.",
  specifications: "IP67 ingress ratings, drop-tested structural armor, non-conductive ESD-safe coatings.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/protective_operational_accessories.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/protective_operational_accessories.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/protective_operational_accessories.png-wvc-sizes",
  imageAlt: "Heavy-duty protective cases and analytical probe shields",
  linkTo: "/ProductsPage",
  iconName: "shield"
}];
export default function ProductCategories() {
  const prefersReducedMotion = useReducedMotion();
  const getCategoryIcon = (iconName: string) => {
    switch (iconName) {
      case "package":
        return <PackageCheck className="size-5 text-primary" />;
      case "layers":
        return <Layers className="size-5 text-primary" />;
      case "shield":
        return <ShieldCheck className="size-5 text-primary" />;
      default:
        return <CheckCircle2 className="size-5 text-primary" />;
    }
  };
  return <section id="productcategoriesgrid" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 border-b border-border/60">
      <div className="page-container">
        {/* Section Header */}
        <div className="max-w-3xl mb-14 lg:mb-18">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary text-secondary-foreground text-xs font-medium uppercase tracking-wider mb-4 border border-border">
            Laboratory & Process Hardware
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground mb-4">
            Essential Consumables & Operational Accessories
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            Standardized accessories, sampling kits, and field-ready consumables formulated to support reliable analytical performance in pharmaceutical, chemical, and industrial inspection environments.
          </p>
        </div>

        {/* 3-Column Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {categories.map((category, i) => <motion.div key={category.id} data-index={i} initial={prefersReducedMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: "-50px"
        }} transition={{
          duration: 0.45,
          delay: i * 0.12,
          ease: "easeOut"
        }} className="flex">
              <Card className="flex flex-col justify-between w-full h-full border border-border bg-card shadow-sm hover:shadow-md transition-shadow overflow-hidden rounded-xl">
                <div>
                  {/* Category Image */}
                  <div className="relative aspect-16/10 w-full overflow-hidden bg-muted border-b border-border">
                    <img src={category.imageSrc} data-wvc-srcset={category.imageSrcset} data-wvc-sizes={category.imageSizes} alt={category.imageAlt} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" />
                    <div className="absolute top-3 left-3">
                      <Badge variant="secondary" className="shadow-xs bg-background/95 text-foreground border border-border backdrop-blur-xs">
                        {category.badge}
                      </Badge>
                    </div>
                  </div>

                  <CardHeader className="pt-6 pb-3">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="p-2 rounded-lg bg-secondary border border-border">
                        {getCategoryIcon(category.iconName)}
                      </div>
                      <CardTitle className="text-xl font-bold font-default text-card-foreground">
                        {category.title}
                      </CardTitle>
                    </div>
                    <CardDescription className="text-sm text-muted-foreground leading-normal mt-1">
                      {category.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="pt-0 space-y-4 text-sm">
                    {/* Neutral Factual Use Case */}
                    <div className="p-3.5 rounded-lg bg-muted/60 border border-border/80">
                      <span className="block text-xs font-semibold uppercase tracking-wider text-foreground mb-1">
                        Functional Use Case
                      </span>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        {category.useCase}
                      </p>
                    </div>

                    {/* Technical Specifications */}
                    <div className="border-t border-border pt-3">
                      <span className="block text-xs font-medium text-muted-foreground mb-1">
                        Technical Criteria:
                      </span>
                      <p className="text-xs font-medium text-foreground">
                        {category.specifications}
                      </p>
                    </div>
                  </CardContent>
                </div>

                <CardFooter className="pt-2 pb-6 border-t border-border/60">
                  <Button asChild variant="outline" className="w-full justify-between group">
                    <Link to={category.linkTo}>
                      <span>Review Specifications</span>
                      <ArrowRight className="size-4 text-muted-foreground transition-transform group-hover:translate-x-1 group-hover:text-foreground" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </div>

        {/* Bottom Context Banner */}
        <div className="mt-12 lg:mt-16 p-6 rounded-xl border border-border bg-secondary/40 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="space-y-1">
            <h3 className="text-base font-semibold font-default text-foreground">
              Custom batch packaging or regulated batch certifications needed?
            </h3>
            <p className="text-sm text-muted-foreground">
              Technical documentation, Certificates of Analysis (CoA), and validation packs are available on request.
            </p>
          </div>
          <Button asChild variant="default" className="shrink-0">
            <Link to="/ContactPage">
              Request Certification
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}